// lib
import React from 'react'
import PropTypes from 'prop-types'
import { List, Map, fromJS } from 'immutable'

import ADND from '@aytm/lib/adnd/ADND'

import ContextSubscriber from '@aytm/lib/common/components/decorators/ContextSubscriber'
import ContextProvider from '@aytm/lib/common/components/decorators/ContextProvider'
import ManagedRenderComponent from '@aytm/lib/common/components/ManagedRenderComponent'

import { REPORTS_HELP } from '@aytm/app/stats/page/sidebar/components/lib/helps'

import { generateUID } from '@aytm/app/stats/lib/helpers'
import {
  reportStatePath,
  dropListItemDefault,
  dropListItemAutomated,
  dropListItemGuided,
  dropListItemOther,
  addListItem,
  addSkipperGuidedListItem,
  ensureSkipperGuidedGeneratingDraft,
  adoptSkipperGuidedDraftReport,
  markSkipperGuidedDraftReady
} from '@aytm/app/stats/page/sidebar/reports/actions'

import { GUIDED_REPORT_ERROR_NOTICE, SKIPPER_REPORT_FEATURE } from './lib/skipperGuidedReportsConst'

import {
  isSkipperGuidedReport,
  isSkipperGuidedDraftReport,
  isSkipperReportPurchased
} from '@aytm/app/stats/page/sidebar/reports/lib/skipperGuidedReport'

import { MAX_INFLIGHT_REPORTS } from '@aytm/app/stats/page/sidebar/reports/lib/skipperGuidedReportStatus'

// components
import { Button } from 'primereact/button'

import Report from '@aytm/app/stats/page/sidebar/reports/Report'

import SidebarPopup from '@aytm/app/stats/page/sidebar/components/SidebarPopup'
import SkipperGuidedReportButton from '@aytm/app/stats/page/sidebar/reports/components/SkipperGuidedReportButton'

import { openGuidedReportsChat } from '@aytm/app/stats/stores/guidedReportsEntry'
import { createGuidedReportGenerationPoll, generationReportId } from '@aytm/app/stats/stores/guidedReportGenerationPoll'
import { generationPollKey, createGuidedReportJobPoll } from '@aytm/app/stats/stores/guidedReportJobPoll'
import {
  TRACKING_RANK,
  claimGuidedReportTracking,
  releaseGuidedReportTracking
} from '@aytm/app/stats/stores/guidedReportTracking'

// styles
import '@aytm/app/stats/page/styles/globals.scss'

import style from '@aytm/app/stats/page/sidebar/reports/styles/ReportsPanel.module.scss'

// <Component>
// eslint-disable-next-line no-class-assign
class ReportsPanel extends ManagedRenderComponent {
  static propTypes = {
    root: PropTypes.any.isRequired,
    url: PropTypes.string.isRequired,
    state: PropTypes.instanceOf(Map).isRequired,
    model: PropTypes.instanceOf(Map).isRequired,
    limits: PropTypes.instanceOf(Map).isRequired,
    actions: PropTypes.object.isRequired,
    features: PropTypes.object,
    windowUID: PropTypes.string.isRequired,
    currentReportId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    virtualReportsIds: PropTypes.array.isRequired,
    shareLinkHash: PropTypes.string,
    timeZone: PropTypes.instanceOf(Map),
    disabled: PropTypes.bool,
    disabledDataLab: PropTypes.bool,
    isAdmin: PropTypes.bool,
    isSurveyOwner: PropTypes.bool,
    isSurveyGuest: PropTypes.bool,
    isSurveyViewer: PropTypes.bool,
    isSurveyCompleted: PropTypes.bool,
    getReportFilters: PropTypes.func,
    onLoad: PropTypes.func
  }
  static updateOnProps(nextProps, prevProps) {
    return (
      nextProps.name != prevProps.name ||
      nextProps.state != prevProps.state ||
      nextProps.model != prevProps.model ||
      nextProps.limits != prevProps.limits ||
      nextProps.currentReportId != prevProps.currentReportId ||
      nextProps.disabled != prevProps.disabled ||
      nextProps.disabledDataLab != prevProps.disabledDataLab
    )
  }

  constructor(props, context) {
    super(props, context)

    this.dropProps = this.dropProps.bind(this)
    this.togglePlaceholder = this.togglePlaceholder.bind(this)

    this.saveReportTypeRef = this.saveReportTypeRef.bind(this)
    this.saveReportPositionRef = this.saveReportPositionRef.bind(this)

    this.saveDefaultListRef = this.saveDefaultListRef.bind(this)
    this.saveAutomatedListRef = this.saveAutomatedListRef.bind(this)
    this.saveGuidedListRef = this.saveGuidedListRef.bind(this)
    this.saveOtherListRef = this.saveOtherListRef.bind(this)

    this.renderItems = this.renderItems.bind(this)
    this.renderList = this.renderList.bind(this)
    this.renderAddItem = this.renderAddItem.bind(this)

    this.dndDefaultItemsList = ADND.targets
      .itemsList(
        {
          acceptTypes: [
            `report-${props.windowUID}`,
            `report-${props.windowUID}-skipper`,
            `report-${props.windowUID}-concept`,
            `report-${props.windowUID}-skipper-guided`
          ]
        },
        { capture: false }
      )
      .buildPlaceholder(() => this.onDNDBuildPlaceholder(this.refDefaultList))
      .onUpdateItemPositions(({ placeholderIndex }) =>
        this.onDNDUpdateItemPositions({ placeholderIndex, refList: this.refDefaultList })
      )
      .onEnter(({ placeholderIndex }) =>
        this.onDNDUpdateItemPositions({ placeholderIndex, refList: this.refDefaultList })
      )
      .onLeave(({ placeholderIndex }) =>
        this.onDNDUpdateItemPositions({ placeholderIndex, refList: this.refDefaultList })
      )
      .onDrop(this.onDNDDropDefault.bind(this))

    this.dndAutomatedItemsList = ADND.targets
      .itemsList(
        {
          acceptTypes: [
            `report-${props.windowUID}-skipper`,
            `report-${props.windowUID}-skipper-hidden`,
            `report-${props.windowUID}-concept`,
            `report-${props.windowUID}-concept-hidden`
          ]
        },
        { capture: false }
      )
      .buildPlaceholder(() => this.onDNDBuildPlaceholder(this.refAutomatedList))
      .onUpdateItemPositions(({ placeholderIndex }) =>
        this.onDNDUpdateItemPositions({ placeholderIndex, refList: this.refAutomatedList })
      )
      .onEnter(({ placeholderIndex }) =>
        this.onDNDUpdateItemPositions({ placeholderIndex, refList: this.refAutomatedList })
      )
      .onLeave(({ placeholderIndex }) =>
        this.onDNDUpdateItemPositions({ placeholderIndex, refList: this.refAutomatedList })
      )
      .onDrop(this.onDNDDropAutomated.bind(this))

    this.dndGuidedItemsList = ADND.targets
      .itemsList(
        {
          acceptTypes: [`report-${props.windowUID}-skipper-guided`, `report-${props.windowUID}-skipper-guided-hidden`]
        },
        { capture: false }
      )
      .buildPlaceholder(() => this.onDNDBuildPlaceholder(this.refGuidedList))
      .onUpdateItemPositions(({ placeholderIndex }) =>
        this.onDNDUpdateItemPositions({ placeholderIndex, refList: this.refGuidedList })
      )
      .onEnter(({ placeholderIndex }) =>
        this.onDNDUpdateItemPositions({ placeholderIndex, refList: this.refGuidedList })
      )
      .onLeave(({ placeholderIndex }) =>
        this.onDNDUpdateItemPositions({ placeholderIndex, refList: this.refGuidedList })
      )
      .onDrop(this.onDNDDropGuided.bind(this))

    this.dndOtherItemsList = ADND.targets
      .itemsList(
        {
          acceptTypes: [`report-${props.windowUID}`, `report-${props.windowUID}-hidden`]
        },
        { capture: false }
      )
      .buildPlaceholder(() => this.onDNDBuildPlaceholder(this.refOtherList))
      .onUpdateItemPositions(({ placeholderIndex }) =>
        this.onDNDUpdateItemPositions({ placeholderIndex, refList: this.refOtherList })
      )
      .onEnter(({ placeholderIndex }) =>
        this.onDNDUpdateItemPositions({ placeholderIndex, refList: this.refOtherList })
      )
      .onLeave(({ placeholderIndex }) =>
        this.onDNDUpdateItemPositions({ placeholderIndex, refList: this.refOtherList })
      )
      .onDrop(this.onDNDDropOther.bind(this))
  }

  togglePlaceholder(placeholderIndex, refList) {
    const placeholder = refList.querySelector(`.${style.placeholder}`)
    if (!placeholder) return

    if (
      0 == placeholderIndex &&
      1 == this.refReportPosition &&
      refList.classList.contains('stats-ReportsPanel-list-other')
    ) {
      placeholder.style.display = 'none'
    } else placeholder.style.display = null === placeholderIndex ? 'none' : 'block'
  }

  onDNDBuildPlaceholder(refList = null) {
    const placeholderNode = document.createElement('div')
    placeholderNode.className = style.placeholder
    if (null !== this.refReportType) {
      placeholderNode.classList.add(style[`placeholder-${this.refReportType}`])
    }
    if (refList.classList.contains('stats-ReportsPanel-list-other')) {
      placeholderNode.style.display = 'none'
    }
    return placeholderNode
  }

  onDNDUpdateItemPositions({ placeholderIndex, refList = null } = {}) {
    if (null === refList) return

    this.togglePlaceholder(placeholderIndex, refList)
    setTimeout(() => {
      this.togglePlaceholder(placeholderIndex, refList)
    }, 10)
  }

  dropProps() {
    const { url, state: listState, model: listModel, actions } = this.props
    return { url, listState, listModel, actions }
  }

  onDNDDropDefault(dropData) {
    const { model } = dropData.item

    dropListItemDefault({
      ...this.dropProps(),
      listIndex: 0,
      model: Object.assign({}, model)
    })
  }

  onDNDDropAutomated(dropData) {
    const { model } = dropData.item

    dropListItemAutomated({
      ...this.dropProps(),
      model: Object.assign({}, model)
    })
  }

  onDNDDropGuided(dropData) {
    const { model } = dropData.item

    dropListItemGuided({
      ...this.dropProps(),
      listIndex: dropData.position,
      model: Object.assign({}, model)
    })
  }

  onDNDDropOther(dropData) {
    const { model } = dropData.item

    dropListItemOther({
      ...this.dropProps(),
      listIndex: dropData.position,
      model: Object.assign({}, model)
    })
  }

  saveReportTypeRef(ref) {
    this.saveRef('ReportType')(ref)
  }

  saveReportPositionRef(ref) {
    this.saveRef('ReportPosition')(ref)
  }

  saveDefaultListRef(ref) {
    this.saveRef('DefaultList')(ref)
    this.dndDefaultItemsList.connect(ref)
  }

  saveAutomatedListRef(ref) {
    this.saveRef('AutomatedList')(ref)
    this.dndAutomatedItemsList.connect(ref)
  }

  saveGuidedListRef(ref) {
    this.saveRef('GuidedList')(ref)
    this.dndGuidedItemsList.connect(ref)
  }

  saveOtherListRef(ref) {
    this.saveRef('OtherList')(ref)
    this.dndOtherItemsList.connect(ref)
  }

  componentDidMount() {
    const { onLoad } = this.props
    'function' == typeof onLoad && onLoad()
  }

  componentDidUpdate() {
    const { onLoad } = this.props
    'function' == typeof onLoad && onLoad()
  }

  renderItems(items, d, dndItemsList) {
    const { state: listState } = this.props

    return items.map((model, itemIndex) => (
      <Report
        key={model.get('id')}
        state={listState.getIn(reportStatePath(model.get('id'))) || Map()}
        model={model}
        position={itemIndex + d}
        dndItemsList={dndItemsList}
        saveReportTypeRef={this.saveReportTypeRef}
        saveReportPositionRef={this.saveReportPositionRef}
      />
    ))
  }

  renderList() {
    const { model, virtualReportsIds, isSurveyGuest } = this.props
    const items = model.get('items')
    const itemsSeq = items.toList()

    const defaultItemsSeq = itemsSeq.filter(r => !virtualReportsIds.includes(r.get('type'))).slice(0, 1)
    const defaultItemsSeqLength = defaultItemsSeq.size

    const skipperItemsSeq = itemsSeq.slice(1).filter(r => 'skipper' == r.get('type') && !isSkipperGuidedReport(r))
    const skipperItemsSeqLength = skipperItemsSeq.size

    const conceptItemsSeq = itemsSeq.slice(1).filter(r => 'concept' == r.get('type'))
    const conceptItemsSeqLength = conceptItemsSeq.size

    const virtualItemsSeq = itemsSeq.slice(1).filter(r => virtualReportsIds.includes(r.get('type')))
    const virtualItemsSeqLength = virtualItemsSeq.size

    const skipperGuidedItemsSeq = itemsSeq.slice(1).filter(r => 'skipper' == r.get('type') && isSkipperGuidedReport(r))
    const skipperGuidedItemsSeqLength = skipperGuidedItemsSeq.size

    const otherItemsSeq = itemsSeq
      .slice(1)
      .filter(r => !['skipper', 'concept', ...virtualReportsIds].includes(r.get('type')))
    const otherItemsSeqLength = otherItemsSeq.size

    return (
      <>
        {0 < defaultItemsSeqLength ? (
          <div
            ref={this.saveDefaultListRef || this.saveRef('DefaultList')}
            className={`${style.list} ${style['list-default']}`}
          >
            <div className={`${style.label} ${style['label-default']}`}>Default Report</div>
            {this.renderItems(defaultItemsSeq, 1, this.dndDefaultItemsList)}
          </div>
        ) : null}
        {0 < skipperItemsSeqLength || 0 < conceptItemsSeqLength || 0 < virtualItemsSeqLength ? (
          <div
            ref={this.saveAutomatedListRef || this.saveRef('AutomatedList')}
            className={`${style.list} ${style['list-automated']}`}
          >
            <div className={style.label}>Automated Reports</div>
            {0 < skipperItemsSeqLength
              ? this.renderItems(skipperItemsSeq, defaultItemsSeqLength + 1, this.dndAutomatedItemsList)
              : null}
            {0 < conceptItemsSeqLength
              ? this.renderItems(
                  conceptItemsSeq,
                  defaultItemsSeqLength + skipperItemsSeqLength + 1,
                  this.dndAutomatedItemsList
                )
              : null}
            {0 < virtualItemsSeqLength
              ? this.renderItems(
                  virtualItemsSeq,
                  defaultItemsSeqLength + skipperItemsSeqLength + conceptItemsSeqLength + 1
                )
              : null}
          </div>
        ) : null}
        {0 < skipperGuidedItemsSeqLength ? (
          <div
            ref={this.saveGuidedListRef || this.saveRef('GuidedList')}
            className={`${style.list} ${style['list-guided']}`}
          >
            <div className={style.label}>Guided Reports</div>
            {this.renderItems(
              skipperGuidedItemsSeq,
              defaultItemsSeqLength + skipperItemsSeqLength + conceptItemsSeqLength + virtualItemsSeqLength + 1,
              this.dndGuidedItemsList
            )}
          </div>
        ) : null}
        {0 < otherItemsSeqLength && !isSurveyGuest ? (
          <div
            ref={this.saveOtherListRef || this.saveRef('OtherList')}
            className={`${style.list} ${style['list-other']}`}
          >
            <div className={style.label}>Other Reports</div>
            {this.renderItems(
              otherItemsSeq,
              defaultItemsSeqLength +
                skipperItemsSeqLength +
                conceptItemsSeqLength +
                virtualItemsSeqLength +
                skipperGuidedItemsSeqLength +
                1,
              this.dndOtherItemsList
            )}
          </div>
        ) : null}
      </>
    )
  }

  renderAddItem() {
    const { state, model, currentReportId } = this.props
    const itemModel = model.getIn(['items', model.get('items').findIndex(item => item.get('id') == currentReportId)])

    return <AddReport state={state.getIn(reportStatePath(itemModel.get('id'))) || Map()} model={itemModel} />
  }

  render() {
    const { root, model } = this.props

    return (
      <div className={style.component}>
        <div className={style['component-title']}>
          Reports
          <SidebarPopup id="reports" root={root} help={REPORTS_HELP} />
        </div>

        {0 < model.get('items').size ? (
          <>
            {this.renderAddItem()}
            {this.renderList()}
          </>
        ) : null}
      </div>
    )
  }
}

ReportsPanel = ContextProvider({
  name: 'reportList',
  propTypes: {
    root: PropTypes.any.isRequired,
    url: PropTypes.string.isRequired,
    state: PropTypes.instanceOf(Map).isRequired,
    model: PropTypes.instanceOf(Map).isRequired,
    limits: PropTypes.instanceOf(Map).isRequired,
    actions: PropTypes.object.isRequired,
    windowUID: PropTypes.string.isRequired,
    currentReportId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    virtualReportsIds: PropTypes.array.isRequired,
    shareLinkHash: PropTypes.string,
    timeZone: PropTypes.instanceOf(Map),
    disabled: PropTypes.bool,
    disabledDataLab: PropTypes.bool,
    isAdmin: PropTypes.bool,
    isSurveyOwner: PropTypes.bool,
    isSurveyGuest: PropTypes.bool,
    isSurveyViewer: PropTypes.bool,
    isSurveyCompleted: PropTypes.bool,
    features: PropTypes.object,
    getReportFilters: PropTypes.func
  },
  contextTypes: {
    root: PropTypes.any.isRequired,
    url: PropTypes.string.isRequired,
    state: PropTypes.instanceOf(Map).isRequired,
    model: PropTypes.instanceOf(Map).isRequired,
    limits: PropTypes.instanceOf(Map).isRequired,
    actions: PropTypes.object.isRequired,
    windowUID: PropTypes.string.isRequired,
    currentReportId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    virtualReportsIds: PropTypes.array.isRequired,
    shareLinkHash: PropTypes.string,
    timeZone: PropTypes.instanceOf(Map),
    disabled: PropTypes.bool,
    disabledDataLab: PropTypes.bool,
    isAdmin: PropTypes.bool,
    isSurveyOwner: PropTypes.bool,
    isSurveyGuest: PropTypes.bool,
    isSurveyViewer: PropTypes.bool,
    isSurveyCompleted: PropTypes.bool,
    features: PropTypes.object,
    getReportFilters: PropTypes.func
  },
  values(props) {
    return {
      root: props.root,
      url: props.url,
      state: props.state,
      model: props.model,
      limits: props.limits,
      actions: props.actions,
      windowUID: props.windowUID,
      currentReportId: props.currentReportId,
      virtualReportsIds: props.virtualReportsIds,
      shareLinkHash: props.shareLinkHash,
      timeZone: props.timeZone,
      disabled: props.disabled,
      disabledDataLab: props.disabledDataLab,
      isAdmin: props.isAdmin,
      isSurveyOwner: props.isSurveyOwner,
      isSurveyGuest: props.isSurveyGuest,
      isSurveyViewer: props.isSurveyViewer,
      isSurveyCompleted: props.isSurveyCompleted,
      features: props.features,
      getReportFilters: props.getReportFilters
    }
  },
  shouldUpdate(nextProps, prevProps) {
    return (
      nextProps.state != prevProps.state ||
      nextProps.model != prevProps.model ||
      nextProps.limits != prevProps.limits ||
      nextProps.currentReportId != prevProps.currentReportId ||
      nextProps.disabled != prevProps.disabled ||
      nextProps.disabledDataLab != prevProps.disabledDataLab
    )
  }
})(ReportsPanel)

export default ReportsPanel

// <Component>
// eslint-disable-next-line no-class-assign
class AddReport extends ManagedRenderComponent {
  static propTypes = {
    state: PropTypes.instanceOf(Map).isRequired,
    model: PropTypes.instanceOf(Map).isRequired
  }
  static updateOnProps(nextProps, prevProps) {
    return (
      nextProps.state != prevProps.state ||
      nextProps.model != prevProps.model ||
      nextProps.listState != prevProps.listState ||
      nextProps.limits != prevProps.limits ||
      nextProps.currentReportId != prevProps.currentReportId ||
      nextProps.disabled != prevProps.disabled ||
      nextProps.disabledDataLab != prevProps.disabledDataLab
    )
  }

  constructor(...args) {
    super(...args)
    this.onReportAdd = this.onReportAdd.bind(this)
    this.onBlankReportAdd = this.onBlankReportAdd.bind(this)
    this.onSkipperGuidedReportAdd = this.onSkipperGuidedReportAdd.bind(this)

    // Watches the chat for the launch handle so a just-created draft can flip to
    // "generating" in-session. Read this.props inside onGeneration — it fires later.
    this.generationPoll = null
    // Tracks the stat-server generation job (via the handle's polling_url) to its
    // terminal state so the draft can flip to "ready" / revert on failure.
    this.jobPoll = null
    // Identity of the generation the job poll is tracking — so the same handle
    // arriving again does not restart the poll; only a new generation does.
    this.polledGenerationKey = null
    // The report this panel claimed for the job poll, released when the poll ends.
    this.jobPollReportId = null
  }

  startGuidedReportGenerationPoll() {
    if (this.generationPoll) return // already watching this survey's dispatch

    this.generationPoll = createGuidedReportGenerationPoll({
      onGeneration: (generation, { recovered }) => {
        // Read before the flip below, which re-ids a waiting draft onto this very report.
        const known = this.holdsReport(generationReportId(generation))

        // Claimed before the flip, not after: the flip's list write reaches the host synchronously
        // and arms the id-based status poll, which reads the claim to decide whether to start. A
        // report the list already holds is left to that poll — taking its job would silence the
        // watcher the flip just armed, on a row this poll does not need to own.
        // The row is not selected: switching to it would blank the user's charts while the
        // generation only has a placeholder body. The poll keeps running — a session can
        // dispatch another generation after this one.
        if (!known) this.startGuidedReportJobPoll(generation)

        // Create-or-flip: flip a waiting draft, flip the report a regeneration targets, or
        // materialise a generating placeholder when the handle is recovered on mount with no draft.
        ensureSkipperGuidedGeneratingDraft({
          listModel: this.props.listModel,
          actions: this.props.actions,
          generation,
          recovered
        })
      }
    })
    this.generationPoll.start()
  }

  holdsReport(reportId) {
    if (!reportId) return false
    return 0 <= this.props.listModel.get('items').findIndex(item => reportId == item.get('id'))
  }

  // Consume the launch handle's polling_url to track the generation in-session.
  // On completion flip the draft to "ready" (the row offers a view affordance);
  // on failure revert it so the user can retry. No handle URL ⇒ nothing to poll.
  // Idempotent on the handle identity — the same generation received again does
  // not restart the poll; only a new generation does.
  startGuidedReportJobPoll(generation) {
    const key = generationPollKey(generation)
    if (!key || key === this.polledGenerationKey) return
    if (this.jobPoll) return // a generation is already being tracked

    const reportId = generationReportId(generation)
    // This poll outranks the status poll, so it displaces one already watching the report rather
    // than standing down: only this one carries the persisted report and writes a terminal state.
    // A second poll of its own rank is still refused — reopening the chart re-delivers the handle.
    const claimed = claimGuidedReportTracking(reportId, {
      rank: TRACKING_RANK.generation,
      stop: () => this.releaseGuidedReportJobPoll()
    })
    if (reportId && !claimed) return

    this.polledGenerationKey = key
    this.jobPollReportId = claimed ? reportId : null

    this.jobPoll = createGuidedReportJobPoll({
      pollingUrl: generation.polling_url,
      jobId: generation.job_id,
      onReport: report => {
        // This reading is what the job poll was for — it carries the report the stat server
        // persisted at launch. Once that is adopted the id-based status poll can watch the rest of
        // the lifecycle by id alone, so the report goes back here, before the write below: that
        // write is what arms the status poll, and a claim still held would refuse it.
        this.releaseGuidedReportJobPoll()

        adoptSkipperGuidedDraftReport({
          listModel: this.props.listModel,
          actions: this.props.actions,
          reportId,
          report
        })
        // The reading swaps the placeholder skeleton for the server's body, and a user sitting on
        // that row is looking at an area drawn from the skeleton.
        this.props.actions.repaintReport?.(reportId)
      },
      onReady: report => {
        // Flip the client placeholder to "ready" and take the finished report the poll carries —
        // the row then offers the view affordance.
        markSkipperGuidedDraftReady({
          listModel: this.props.listModel,
          actions: this.props.actions,
          reportId,
          report
        })
        this.releaseGuidedReportJobPoll()
        // nothing else draws the body that just landed on a row the user is already reading
        this.props.actions.repaintReport?.(reportId)
      },
      // A newer turn superseded this generation: the row stays 'generating' untouched and the
      // claim goes back, so the winning generation may be watched on it.
      onSuperseded: () => this.releaseGuidedReportJobPoll(),
      onError: () => {
        // A failure settles the row for viewing like a completion does, and for the same reason:
        // the report the stat server persisted at launch is what holds the generation's own
        // failure note, and the view affordance is the only thing that opens it. The status alone
        // then tells the popup this report kept no settings — no separate failure mark is needed.
        markSkipperGuidedDraftReady({ listModel: this.props.listModel, actions: this.props.actions, reportId })
        this.releaseGuidedReportJobPoll()

        if ('function' == typeof window.notifyStickyShow) {
          window.notifyStickyShow('guided-report-error', 'Skipper Report', GUIDED_REPORT_ERROR_NOTICE, 'ico/error.png')
        }
      }
    })
    this.jobPoll.start()
  }

  // Drop the job poll and hand the report back, so the status poll may watch it again.
  releaseGuidedReportJobPoll() {
    this.jobPoll?.stop()
    this.jobPoll = null
    releaseGuidedReportTracking(this.jobPollReportId)
    this.jobPollReportId = null
  }

  onReportAdd() {
    const { url, state, model, listState, listModel, actions } = this.props

    addListItem({
      url,
      listState,
      listModel,
      state,
      model,
      actions
    })
  }

  onBlankReportAdd() {
    const { url, listState, listModel, actions } = this.props

    const index = listModel.get('items').findIndex(v => 'original' == v.get('type'))

    const model = listModel.getIn(['items', index]).withMutations(map => {
      map
        .set('params', new Map())
        .set('config', new Map())
        .set(
          'virtual',
          fromJS({
            vq1: {
              id: 'vq1',
              text: 'Virtual Question',
              type: 'dlv',
              variant: 'rb',
              answerVariants: [
                {
                  text: '',
                  pattern: '',
                  position: 1,
                  options: {
                    uid: generateUID(),
                    mode: 'basic'
                  }
                },
                {
                  text: '',
                  pattern: '',
                  position: 2,
                  options: {
                    uid: generateUID(),
                    mode: 'basic'
                  }
                }
              ],
              position: 0
            }
          })
        )
        .set('schema', new List(['vq1']))
        .set('storytellings', new Map())
        .set('storytellingNotes', new Map())
        .set('logicData', new Map())
        .set('scripts', null)
    })

    addListItem({
      url,
      listState,
      listModel,
      model,
      actions
    })
  }

  onSkipperGuidedReportAdd() {
    const { url, listState, listModel, actions } = this.props

    // Only one Skipper draft should exist at a time — if one is already present,
    // switch to it instead of spawning a duplicate; otherwise notify the Backbone
    // host (Story-3 banner, Task 7) by adding a new draft entity.
    const draft = listModel.get('items').find(item => isSkipperGuidedDraftReport(item))

    if (draft) {
      actions.updateCurrentReportId(draft.get('id'))
    } else {
      addSkipperGuidedListItem({
        url,
        listState,
        listModel,
        actions
      })
      this.startGuidedReportGenerationPoll()
    }

    openGuidedReportsChat({ entry: 'create' })
  }

  componentDidMount() {
    // Recover a generation dispatched before this mount (a reload mid-generation). The handle is
    // only acted on while fresh, and ensureSkipperGuidedGeneratingDraft ignores one whose report
    // has already landed, so a completed generation cannot resurrect a placeholder.
    if (isSkipperReportPurchased(this.props.features?.[SKIPPER_REPORT_FEATURE])) {
      this.startGuidedReportGenerationPoll()
    }
  }

  componentWillUnmount() {
    this.generationPoll?.stop()
    this.generationPoll = null
    this.releaseGuidedReportJobPoll()
  }

  render() {
    const {
      model,
      listState,
      listModel,
      limits,
      disabled,
      disabledDataLab,
      isSurveyGuest,
      isSurveyViewer,
      isSurveyCompleted,
      features
    } = this.props
    const schema = model.get('schema')
    const isAdding = true === listState.get('add')
    const isXpert = ['xpert', 'xpert_custom'].includes(model.get('type'))
    const isEmpty = null !== schema && !schema.size && !isXpert
    const isDisabled =
      true === listState.get('disabled') || listModel.get('items').size >= limits.getIn(['reports', 'max']) || disabled
    const isLocked = ['widget'].includes(model.get('type')) || isAdding || isSurveyGuest || isSurveyViewer
    const isBlankAllowed = !disabledDataLab && !isSurveyGuest && !isSurveyViewer
    // Mirror the backend 3-in-flight cap: every Skipper Report in draft/generating counts,
    // the auto-launched one included.
    const inflightSkipperCount = listModel
      .get('items')
      .filter(item => 'skipper' == item.get('type') && ['draft', 'generating'].includes(item.get('status'))).size

    return isSurveyGuest || isSurveyViewer ? null : (
      <div className={`${style.header} p-fluid`}>
        {features?.[SKIPPER_REPORT_FEATURE]?.hidden ? (
          <div className="p-field p-col-12">
            <Button
              className="p-button-stat p-button-rounded"
              label="Save as a new report"
              icon={<i className="icon-aytmfd-report-v2" />}
              disabled={isEmpty || isDisabled || isLocked}
              onClick={isEmpty || isDisabled || isLocked ? null : this.onReportAdd}
            />
          </div>
        ) : (
          <SkipperGuidedReportButton
            feature={features?.[SKIPPER_REPORT_FEATURE]}
            betaFree={Boolean(features?.[SKIPPER_REPORT_FEATURE]?.beta)}
            atInflightCap={inflightSkipperCount >= MAX_INFLIGHT_REPORTS}
            atReportsCap={listModel.get('items').size >= limits.getIn(['reports', 'max'])}
            isSurveyCompleted={isSurveyCompleted}
            onCreate={this.onSkipperGuidedReportAdd}
          />
        )}
        {isBlankAllowed ? (
          <div className="p-field p-col-12">
            <Button
              className="p-button-stat p-button-rounded p-button-secondary"
              label="Create a blank report"
              icon={<i className="icon-aytmfd-report-v2" />}
              disabled={isXpert || isDisabled || isLocked}
              onClick={isXpert || isDisabled || isLocked ? null : this.onBlankReportAdd}
            />
          </div>
        ) : null}
      </div>
    )
  }
}

AddReport = ContextSubscriber({
  name: 'reportList',
  contextTypes: {
    url: PropTypes.string.isRequired,
    state: PropTypes.instanceOf(Map).isRequired,
    model: PropTypes.instanceOf(Map).isRequired,
    limits: PropTypes.instanceOf(Map).isRequired,
    actions: PropTypes.object.isRequired,
    currentReportId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    disabled: PropTypes.bool,
    disabledDataLab: PropTypes.bool,
    isSurveyGuest: PropTypes.bool,
    isSurveyViewer: PropTypes.bool,
    isSurveyCompleted: PropTypes.bool,
    features: PropTypes.object
  },
  mapToProps(values) {
    return {
      url: values.url,
      listState: values.state,
      listModel: values.model,
      limits: values.limits,
      actions: values.actions,
      features: values.features,
      currentReportId: values.currentReportId,
      disabled: values.disabled,
      disabledDataLab: values.disabledDataLab,
      isSurveyGuest: values.isSurveyGuest,
      isSurveyViewer: values.isSurveyViewer,
      isSurveyCompleted: values.isSurveyCompleted
    }
  }
})(AddReport)

export { AddReport }

ADND().connect()
